import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import TestInterface from "@/components/tests/TestInterface";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RotateCcw } from "lucide-react";
import { generatePracticeTest } from "@/services/practiceTestService";
import { useChatActions } from "../ChatActionContext";

type Slice = {
  mode: "bank" | "ai";
  subject: string;
  category?: string | null;
  topic?: string | null;
  count: number;
  difficulty?: "easy" | "medium" | "hard";
};

type SingleSpec = {
  mode: "bank" | "ai";
  grade: string;
  subject: string;
  category?: string | null;
  topic?: string | null;
  count?: number;
  difficulty?: "easy" | "medium" | "hard";
};

type MixedSpec = {
  mixed: true;
  grade: string;
  slices: Slice[];
};

type Spec = SingleSpec | MixedSpec;

type TaggedQuestion = any & { __sliceKey: string; __sliceLabel: string; __subjectId: string | null };

const SUBJECT_ALIASES: Record<string, string[]> = {
  math: ["math", "mathematics", "maths", "numeracy", "arithmetic", "algebra", "geometry", "trigonometry"],
  english: ["english", "language", "literacy", "ela", "grammar", "vocabulary"],
  reading: ["reading", "comprehension", "english", "literacy", "language"],
  writing: ["writing", "essay", "composition", "english", "language"],
  science: ["science", "general"],
};

function normalize(s: string) {
  return s.toLowerCase().replace(/[^a-z]/g, "");
}

function pickSubject(rows: Array<{ id: string; name: string }>, wanted: string) {
  const w = normalize(wanted);
  const exact = rows.find((r) => normalize(r.name) === w);
  if (exact) return exact;
  const partial = rows.find((r) => normalize(r.name).includes(w) || w.includes(normalize(r.name)));
  if (partial) return partial;
  // alias-based match (e.g. "Math" -> "Mathematics", "Reading" -> "English")
  const aliasKey = Object.keys(SUBJECT_ALIASES).find(
    (k) => k === w || SUBJECT_ALIASES[k].some((a) => a === w || w.includes(a)),
  );
  if (aliasKey) {
    for (const alias of SUBJECT_ALIASES[aliasKey]) {
      const hit = rows.find((r) => normalize(r.name).includes(alias));
      if (hit) return hit;
    }
  }
  return null;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

async function resolveSlice(
  grade: string,
  slice: Slice,
): Promise<{ questions: TaggedQuestion[]; subjectId: string | null; label: string }> {
  const { data: gradeSubjects, error: subjErr } = await supabase
    .from("subjects").select("id,name").eq("grade", grade);
  if (subjErr) throw subjErr;
  if (!gradeSubjects?.length) throw new Error(`No subjects available for grade ${grade} yet.`);

  const subject = pickSubject(gradeSubjects, slice.subject);
  if (!subject) {
    throw new Error(
      `No '${slice.subject}' subject for grade ${grade}. Available: ${gradeSubjects.map((s) => s.name).join(", ")}.`,
    );
  }

  let categoryId: string | null = null;
  let categoryName: string | null = null;
  if (slice.category) {
    const { data: cats } = await supabase.from("subjects_categories")
      .select("id,name").eq("subject_id", subject.id);
    const cat = cats?.length ? pickSubject(cats as any, slice.category) : null;
    categoryId = (cat as any)?.id ?? null;
    categoryName = (cat as any)?.name ?? null;
  }
  const label = categoryName ? `${subject.name} · ${categoryName}` : subject.name;
  const sliceKey = `${subject.id}:${categoryId ?? ""}`;
  const wanted = Math.max(1, slice.count ?? 10);

  let questions: any[] = [];
  if (slice.mode === "bank") {
    const runBank = async (withCategory: boolean) => {
      let query = supabase.from("questions").select("*, options(*)").eq("subject_id", subject.id);
      if (withCategory && categoryId) query = query.eq("category_id", categoryId);
      const { data, error } = await query.limit(120);
      if (error) throw error;
      return (data ?? []).filter((q: any) => (q.options?.length ?? 0) >= 2);
    };

    let pool = await runBank(true);
    if (!pool.length && categoryId) pool = await runBank(false);

    if (pool.length) {
      questions = shuffle(pool).slice(0, wanted);
    } else {
      // Nothing curated for this area — fall back to AI generation so the
      // learner still gets a real test instead of an error card.
      questions = await generatePracticeTest({
        grade,
        subjectId: subject.id,
        categoryId,
        topicTitle: slice.topic ?? null,
        focusHint: null,
        count: Math.min(wanted, 10),
        difficulty: slice.difficulty ?? "medium",
      }) as any[];
    }
  } else {
    questions = await generatePracticeTest({
      grade,
      subjectId: subject.id,
      categoryId,
      topicTitle: slice.topic ?? null,
      focusHint: null,
      count: slice.count ?? 5,
      difficulty: slice.difficulty ?? "medium",
    }) as any[];
  }

  if (!questions.length) throw new Error(`No questions available for ${label} right now.`);

  return {
    questions: questions.map((q) => ({ ...q, __sliceKey: sliceKey, __sliceLabel: label, __subjectId: subject.id })),
    subjectId: subject.id,
    label,
  };
}

function interleave(groups: TaggedQuestion[][]): TaggedQuestion[] {
  const out: TaggedQuestion[] = [];
  const max = Math.max(...groups.map((g) => g.length));
  for (let i = 0; i < max; i++) for (const g of groups) if (g[i]) out.push(g[i]);
  return out;
}

export function InlineTestRunner({ spec }: { spec: Spec }) {
  const { user } = useAuth();
  const { reportActivity } = useChatActions();
  const [state, setState] = useState<"loading" | "ready" | "error" | "done">("loading");
  const [questions, setQuestions] = useState<TaggedQuestion[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [breakdown, setBreakdown] = useState<Array<{ label: string; correct: number; total: number }> | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const mixed = "mixed" in spec && spec.mixed;
  const grade = spec.grade;
  const slices: Slice[] = mixed
    ? (spec as MixedSpec).slices
    : [{
        mode: (spec as SingleSpec).mode,
        subject: (spec as SingleSpec).subject,
        category: (spec as SingleSpec).category ?? null,
        topic: (spec as SingleSpec).topic ?? null,
        count: (spec as SingleSpec).count ?? ((spec as SingleSpec).mode === "ai" ? 5 : 10),
        difficulty: (spec as SingleSpec).difficulty,
      }];

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setState("loading"); setErrorMsg("");
      try {
        const resolved = await Promise.all(slices.map((s) => resolveSlice(grade, s)));
        const merged = slices.length > 1 ? interleave(resolved.map((r) => r.questions)) : resolved[0].questions;
        if (!cancelled) { setQuestions(merged); setState("ready"); }
      } catch (e: any) {
        if (!cancelled) { setErrorMsg(e?.message ?? "Failed to load test."); setState("error"); }
      }
    }
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(spec), reloadKey]);

  async function persistResults(perSlice: Record<string, { subjectId: string | null; correct: number; total: number; label: string }>) {
    if (!user) return;
    try {
      const entries = Object.values(perSlice);
      const totalScore = entries.reduce((a, b) => a + b.correct, 0);
      const totalQs = entries.reduce((a, b) => a + b.total, 0);
      let groupId: string | null = null;
      if (entries.length > 1) {
        const { data: grp } = await supabase.from("test_session_groups" as any).insert({
          user_id: user.id,
          label: entries.map((e) => e.label).join(" + "),
          total_score: totalScore,
          total_questions: totalQs,
          metadata: { grade, slices },
        }).select("id").single();
        groupId = (grp as any)?.id ?? null;
      }
      for (const e of entries) {
        if (!e.subjectId) continue;
        await supabase.from("test_sessions").insert({
          user_id: user.id,
          subject_id: e.subjectId,
          grade,
          total_questions: e.total,
          score: e.correct,
          completed: true,
          ...(groupId ? { group_id: groupId } : {}),
        } as any);
      }
    } catch (err) {
      console.error("Failed to persist test results", err);
    }
  }

  if (state === "loading") {
    return <Card><CardContent className="p-6 flex items-center gap-3 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {mixed ? `Building a mixed test across ${slices.length} areas…` : "Loading questions…"}
    </CardContent></Card>;
  }
  if (state === "error") {
    return <Card><CardContent className="p-4 text-sm space-y-3">
      <div className="text-destructive">{errorMsg}</div>
      <Button size="sm" variant="outline" onClick={() => setReloadKey(k => k + 1)}>
        <RotateCcw className="h-3.5 w-3.5 mr-1" /> Retry
      </Button>
    </CardContent></Card>;
  }
  if (state === "done" && breakdown) {
    const total = breakdown.reduce((a, b) => a + b.total, 0);
    const correct = breakdown.reduce((a, b) => a + b.correct, 0);
    return <Card><CardContent className="p-6 space-y-4 text-center">
      <div className="text-2xl font-bold">{correct} / {total}</div>
      {breakdown.length > 1 && (
        <div className="grid gap-2 text-left">
          {breakdown.map((b) => (
            <div key={b.label} className="flex items-center justify-between rounded-md border p-2 text-sm">
              <span>{b.label}</span>
              <Badge variant="secondary">{b.correct} / {b.total}</Badge>
            </div>
          ))}
        </div>
      )}
      <div className="text-sm text-muted-foreground">
        {user ? "Saved to your progress. Ask me what to practice next." : "Sign up to save your progress across sessions."}
      </div>
      <Button size="sm" variant="outline" onClick={() => { setBreakdown(null); setReloadKey(k => k + 1); }}>
        <RotateCcw className="h-3.5 w-3.5 mr-1" /> Try again
      </Button>
    </CardContent></Card>;
  }

  const title = mixed ? `Mixed · ${slices.map((s) => s.subject).join(" + ")}` : slices[0].subject;
  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <TestInterface
        questions={questions}
        subjectName={title}
        categoryName={mixed ? undefined : (slices[0].category ?? undefined)}
        testMode="practice"
        onSubmit={(results) => {
          const perSlice: Record<string, { subjectId: string | null; correct: number; total: number; label: string }> = {};
          for (const r of results) {
            const q = questions.find((qq) => qq.id === r.questionId);
            if (!q) continue;
            const key = q.__sliceKey;
            const bucket = perSlice[key] ?? { subjectId: q.__subjectId, correct: 0, total: 0, label: q.__sliceLabel };
            bucket.total += 1;
            const opt = q.options?.find((o: any) => o.id === r.selectedOptionId);
            if (opt?.is_correct) bucket.correct += 1;
            perSlice[key] = bucket;
          }
          const bd = Object.values(perSlice).map((b) => ({ label: b.label, correct: b.correct, total: b.total }));
          setBreakdown(bd);
          setState("done");
          void persistResults(perSlice);
          const total = bd.reduce((a, b) => a + b.total, 0);
          const correct = bd.reduce((a, b) => a + b.correct, 0);
          const detail =
            bd.length > 1
              ? ` Breakdown: ${bd.map((b) => `${b.label} ${b.correct}/${b.total}`).join(", ")}.`
              : "";
          reportActivity(
            `The user just completed a grade ${grade} ${title} practice test inline: ${correct}/${total} correct.${detail}${user ? " It has been saved to their progress." : " (Guest — not saved.)"}`,
          );
        }}
      />
    </div>
  );
}
